import { useEffect } from 'react';
import { isInputFocused } from '@/lib/dom';
import { IframeShield } from './IframeShield';
import { Window } from './Window';
import { pruneHandles, useWindowStore } from './store';

/**
 * Joins window ids into one string, so the effect below can depend on the
 * *set* of windows rather than on the array. A control character, because it
 * cannot appear in an id — and written as an escape, because a literal one is
 * invisible in an editor and makes the whole file read as binary to grep.
 */
const SEPARATOR = '\u0000';

/**
 * Every open window, plus the drag shield. Stacking is pure z-index, so the
 * DOM order stays stable and raising a window never remounts its app.
 */
export function WindowLayer() {
  const windows = useWindowStore((s) => s.windows);

  // A primitive key, so the effect fires when the set of windows changes but
  // not when one of them merely moves.
  const idKey = windows.map((w) => w.id).join(SEPARATOR);

  useEffect(() => {
    pruneHandles(new Set(idKey ? idKey.split(SEPARATOR) : []));
  }, [idKey]);

  useEffect(() => {
    let frame: number | null = null;

    const onResize = () => {
      // Resize fires in bursts; one re-clamp per frame is plenty.
      if (frame !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        useWindowStore.getState().reclampAll();
      });
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape and F4 belong to whatever the user is typing in.
      if (isInputFocused()) return;
      // One keystroke, one owner: MsgBox closes itself on Escape and calls
      // preventDefault, so acting again here would take a second dialog down.
      if (event.defaultPrevented) return;

      const store = useWindowStore.getState();
      const focused = store.windows.find((w) => w.focused);
      if (!focused) return;

      if (event.key === 'Escape' && focused.dialog) {
        event.preventDefault();
        store.close(focused.id);
        return;
      }

      // Best effort only: most browsers hand Alt+F4 straight to the window
      // manager and the page never sees the keydown at all.
      if (event.altKey && event.key === 'F4') {
        event.preventDefault();
        store.close(focused.id);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      {windows.map((w) => (
        <Window key={w.id} win={w} />
      ))}
      <IframeShield />
    </>
  );
}
